import React, { useEffect, useMemo, useState } from 'react';
import CategoryCard from '@/components/ui/CategoryCard';
import { getCategoryImage, transformCategories } from './categoriesUtils';
import { useProductCategoriesWithSubs } from '@/hooks/useProductCategories';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const CategoriesSectionContainer = () => {
  // Same queryKey ('product-categories-with-subs') as the header nav's useCategoryNavigation()
  // and the Products page's useCategoryFilter() — Header already fetches this on every page,
  // homepage included, so reusing it here (instead of the separate lighter product-categories
  // query this component used to run) eliminates a redundant request at zero extra payload
  // cost. The subcategories this query also carries are unused here (transformCategories only
  // populates subcategories when its `products` arg is passed, which this call doesn't do).
  const { categories = [], isLoading: categoriesLoading } = useProductCategoriesWithSubs();

  // Push the counts RPC past the moment `categories` resolves (which is also when this
  // component re-renders, i.e. exactly when the main thread is busiest) instead of firing
  // it instantly. requestIdleCallback waits for real main-thread idle time rather than a
  // guessed offset; the 2000ms timeout guarantees it still fires on a busy thread, and the
  // setTimeout fallback covers Safari, which has no requestIdleCallback at all.
  const [idleReady, setIdleReady] = useState(false);
  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(() => setIdleReady(true), { timeout: 2000 });
      return () => window.cancelIdleCallback(id);
    }
    const timer = window.setTimeout(() => setIdleReady(true), 2000);
    return () => window.clearTimeout(timer);
  }, []);

  // PERFORMANCE FIX: RPC returns 11 aggregated rows instead of scanning 1,863 products
  const { data: categoryProductCounts } = useQuery({
    queryKey: ['category-product-counts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_category_product_counts');

      if (error) throw error;

      const counts: Record<string, number> = {};
      data.forEach((row: { category_id: string; product_count: number }) => {
        counts[row.category_id] = Number(row.product_count);
      });

      return counts;
    },
    staleTime: 15 * 60 * 1000, // Cache for 15 minutes
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: categories.length > 0 && idleReady,
  });

  const displayCategories = useMemo(() => {
    if (!categories.length) return [];
    return transformCategories(categories, getCategoryImage, null, categoryProductCounts);
  }, [categories, categoryProductCounts]);

  if (categoriesLoading) {
    return (
      <section className="py-8 sm:py-16 bg-gray-50">
        <div className="container-custom">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12">Shop by Category</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6" style={{ minHeight: '620px' }}>
            {/* Matches the .slice(0, 12) cap on real content below (row count can't diverge),
                and mirrors CategoryCard's actual DOM shape — same wrapper/padding, and two
                placeholder lines (name + product count) instead of one — so per-card height
                is a close match too. Real category names vary in length (some wrap to 2 lines),
                so exact pixel parity isn't guaranteed by markup alone — the shared minHeight
                below is the actual guarantee against reflow on swap-in. */}
            {[...Array(12)].map((_, i) => (
              <div key={i} className="rounded-lg border bg-white shadow-sm animate-pulse">
                <div className="p-3 sm:p-4 text-center">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-full mx-auto mb-3 sm:mb-4"></div>
                  <div className="rounded-lg border p-2 sm:p-3">
                    <div className="h-3 sm:h-4 bg-gray-200 rounded w-3/4 mx-auto"></div>
                    <div className="h-2.5 sm:h-3 bg-gray-200 rounded w-1/2 mx-auto mt-1"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-8 sm:py-16 bg-gray-50">
      <div className="container-custom">
        <h2 className="text-2xl sm:text-3xl font-bold text-center mb-8 sm:mb-12">Shop by Category</h2>

        {displayCategories.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6" style={{ minHeight: '620px' }}>
            {displayCategories.slice(0, 12).map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
              />
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500">
            <p>No categories available at the moment. Please check back soon.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default CategoriesSectionContainer;
